import { loadStripe } from "@stripe/stripe-js";
import { Stripe } from "stripe";

interface IData {
  priceLookUpKey: string;
  paymentMethodId: string;

  // using for creating subscription
  subscription?: Stripe.Response<Stripe.Subscription>;

  // below are used for retries
  invoice?: Stripe.Response<Stripe.Invoice>;
  isRetry?: boolean;
}

export async function handlePaymentThatRequiresCustomerAction({
  subscription,
  invoice,
  priceLookUpKey,
  paymentMethodId,
  isRetry,
}: IData) {
  if (subscription?.status === "active") {
    // Subscription is active, no customer actions required.
    return { subscription, priceLookUpKey, paymentMethodId, invoice };
  }

  // If it's a first payment attempt, the payment intent is on the subscription latest invoice.
  // If it's a retry, the payment intent will be on the invoice itself.
  const paymentIntent = invoice
    ? invoice.payment_intent
    : (subscription?.latest_invoice as Stripe.Invoice)?.payment_intent;

  if (!paymentIntent || typeof paymentIntent === "string") {
    // types are a bit wild here, it should return a Stripe.Invoice but i'm just guarding here
    return { subscription, priceLookUpKey, paymentMethodId, invoice };
  }

  if (
    paymentIntent.status === "requires_action" ||
    (isRetry === true && paymentIntent.status === "requires_payment_method")
  ) {
    const stripe = await loadStripe(
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!
    );

    return stripe!
      .confirmCardPayment(paymentIntent.client_secret!, {
        payment_method: paymentMethodId,
      })
      .then((result) => {
        if (result.error) {
          // Start code flow to handle updating the payment details.
          // Display error message in your UI.
          // The card was declined (i.e. insufficient funds, card has expired, etc).
          throw result.error;
        } else {
          if (result.paymentIntent.status === "succeeded") {
            // Show a success message to your customer.
            return {
              priceLookUpKey: priceLookUpKey,
              subscription: subscription,
              invoice: invoice,
              paymentMethodId: paymentMethodId,
            };
          } else {
            // eslint-disable-next-line no-throw-literal
            throw {
              priceLookUpKey: priceLookUpKey,
              subscription: subscription,
              invoice: invoice,
              paymentMethodId: paymentMethodId,
              message:
                "An error occurred: " +
                result.paymentIntent.status +
                ". Please contact support.",
            };
          }
        }
      });
  } else {
    // No customer action needed.
    return { subscription, priceLookUpKey, paymentMethodId, invoice };
  }
}

export function handleRequiresPaymentMethod({
  subscription,
  paymentMethodId,
  priceLookUpKey,
}: Pick<IData, "paymentMethodId" | "subscription" | "priceLookUpKey">) {
  if (subscription?.status === "active") {
    // subscription is active, no customer actions required.
    return { subscription, priceLookUpKey, paymentMethodId };
  } else if (
    (
      (subscription?.latest_invoice as Stripe.Invoice)
        ?.payment_intent as Stripe.PaymentIntent
    )?.status === "requires_payment_method"
  ) {
    // eslint-disable-next-line no-throw-literal
    throw {
      invoiceId: (
        (subscription?.latest_invoice as Stripe.Invoice)
          ?.payment_intent as Stripe.PaymentIntent
      ).id,
      priceLookUpKey,
      paymentMethodId,
      message: "Retry Please",
    };
  } else {
    return { subscription, priceLookUpKey, paymentMethodId };
  }
}

export function onSubscriptionComplete({
  subscription,
  paymentMethodId,
  priceLookUpKey,
  invoice,
}: Pick<
  IData,
  "paymentMethodId" | "subscription" | "priceLookUpKey" | "invoice"
>) {
  // Payment was successful.
  if (subscription?.status === "active") {
    // Change your UI to show a success message to your customer.
    // Call your backend to grant access to your service based on
    // `result.subscription.items.data[0].price.product` the customer subscribed to.
    return { subscription, priceLookUpKey, paymentMethodId, invoice };
  } else {
    // eslint-disable-next-line no-throw-literal
    throw {
      subscription,
      priceLookUpKey,
      paymentMethodId,
      invoice,
      message:
        "Unexpected error occurred. Please ensure your details are correct and submit again",
    };
  }
}
